import type { LucideIcon } from 'lucide-react';

/**
 * Сегментований перемикач — єдиний стиль внутрішньої навігації кабінету.
 *
 * Візуальний зразок — перемикач мови UA/PL/EN у Topbar, тобто вже наявний
 * патерн, а не нова дизайн-система.
 *
 * Два варіанти оформлення:
 *  · `plate` (типовий) — як у перемикача мови: контейнер-пілюля 36/radius 10 з
 *    тонкою обводкою і нейтральним фоном, всередині елементи 28/radius 8;
 *  · `outline` — ті самі елементи, але БЕЗ плашки під ними: кожна кнопка має
 *    власну обводку й прозорий фон. Для рядів, які читаються як окремі
 *    кнопки-переходи, а не як один перемикач стану.
 *
 * Активний — виразніша обводка + основний колір тексту; неактивний —
 * приглушений текст; hover — легке підсвічування.
 *
 * Акцент навмисно НЕЙТРАЛЬНИЙ (--text-muted / --line-strong), без брендового
 * червоного: §6 контракту прямо каже «сірий контур, БЕЗ червоного», а §3
 * лишає червоний за реальною помилкою.
 *
 * `equal` вирівнює елементи в однакову ширину: ряд стає grid-ом з
 * `grid-auto-columns: 1fr`, тож усі колонки набувають ширини найширшого
 * підпису.
 *
 * `iconOnly` — квадратні кнопки 40×40 лише з іконкою; підпис лишається як
 * tooltip (native `title`, той самий механізм, що вже вживає компонент) і як
 * `aria-label`. Розмір іконки — 18/1.75, як у KPI «Прогнозу».
 *
 * Компонент суто презентаційний: маршрутизацію, стан і права викликач тримає
 * у себе й передає сюди лише activeKey та onSelect.
 */
export interface SegmentedTabItem {
  key: string;
  label: string;
  Icon?: LucideIcon;
  title?: string;
  /** Недоступний пункт: клік не спрацьовує, місце в ряду зберігається. */
  disabled?: boolean;
}

interface Props {
  items: SegmentedTabItem[];
  activeKey: string;
  onSelect: (key: string) => void;
  /** Однакова ширина всіх сегментів (за найширшим підписом). */
  equal?: boolean;
  /** `outline` — кнопки з власною обводкою, без спільної плашки. */
  variant?: 'plate' | 'outline';
  /** Квадратні 40×40 лише з іконкою; підпис іде в tooltip. */
  iconOnly?: boolean;
  className?: string;
}

export default function SegmentedTabs({
  items, activeKey, onSelect, equal, variant = 'plate', iconOnly, className,
}: Props) {
  const outline = variant === 'outline';
  const gap = outline ? 8 : 2;
  return (
    <div
      role="tablist"
      className={`${equal ? '' : 'flex'} items-center overflow-x-auto max-w-full ${outline ? '' : 'px-1'} ${className ?? ''}`}
      style={{
        ...(outline
          ? { background: 'transparent', border: 'none' }
          : { height: 36, borderRadius: 10, background: 'var(--surface-2)', border: '1px solid var(--line)' }),
        fontFamily: 'Urbanist, Inter, sans-serif',
        ...(equal
          ? { display: 'grid', gridAutoFlow: 'column', gridAutoColumns: '1fr', alignItems: 'center', columnGap: gap }
          : { columnGap: gap }),
      }}
    >
      {items.map((it) => {
        const on = it.key === activeKey;
        const off = !!it.disabled;
        return (
          <button
            key={it.key}
            role="tab"
            aria-selected={on}
            aria-disabled={off || undefined}
            disabled={off}
            title={it.title ?? it.label}
            onClick={() => { if (!off) onSelect(it.key); }}
            aria-label={iconOnly ? it.label : undefined}
            className={`inline-flex items-center justify-center cursor-pointer transition-colors
                       ${iconOnly ? 'shrink-0' : 'gap-1.5 px-2.5 text-[11.5px] font-medium whitespace-nowrap'}
                       ${!iconOnly && equal ? 'justify-center min-w-0' : ''}
                       ${!iconOnly && !equal ? 'shrink-0' : ''}`}
            style={{
              // Квадрат фіксований: ширина не залежить від підпису, тож ряд
              // лишається рівним незалежно від довжини назв розділів.
              width: iconOnly ? 40 : undefined,
              height: iconOnly ? 40 : outline ? 32 : 28,
              padding: iconOnly ? 0 : undefined,
              borderRadius: 8,
              // Без підпису активний стан має читатись самою заливкою — тому
              // для iconOnly вона помітніша.
              background: on ? `color-mix(in srgb, var(--text) ${iconOnly ? 12 : 6}%, transparent)` : 'transparent',
              // В outline обводка є завжди — кнопка має читатись як кнопка.
              // Її яскравість зрівняна з рамкою перемикача мови (--line-strong),
              // бо ледь помітний --line на щільному тлі SCADA губився; активна
              // підсилює контур ще на крок. У plate обводку носить сама плашка.
              border: `1px solid ${
                on ? (outline ? 'color-mix(in srgb, var(--text) 30%, transparent)' : 'var(--line-strong)')
                   : outline ? 'var(--line-strong)' : 'transparent'
              }`,
              // Недоступний пункт лишається читабельним (--text-dim), а не
              // напівпрозорим: §пункт контракту про disabled-стани.
              color: off ? 'var(--text-dim)' : on ? 'var(--text)' : 'var(--text-muted)',
              cursor: off ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => {
              if (!on && !off) e.currentTarget.style.background = `color-mix(in srgb, var(--text) ${iconOnly ? 8 : 4}%, transparent)`;
            }}
            onMouseLeave={(e) => {
              if (!on) e.currentTarget.style.background = 'transparent';
            }}
          >
            {it.Icon && (
              <it.Icon
                size={iconOnly ? 18 : 13}
                strokeWidth={iconOnly ? 1.75 : 2}
                style={{ flexShrink: 0 }}
              />
            )}
            {/* Без іконки підпис лишається — інакше кнопка була б порожньою. */}
            {(!iconOnly || !it.Icon) && it.label}
          </button>
        );
      })}
    </div>
  );
}
